import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CapsuleGrid,
  Empty,
  Link,
  Rule,
  soundedPointerHandlers,
  ui,
  type CapsuleTitle,
} from '../ui';
import { formatDate } from '@goodbuild/rules/format';
import type { LibraryEntry } from '@api/views';
import { toCapsule, useShelf } from '../catalogue';
import { ApiError, useApi, useApiMutation } from '../api';
import { Field, Notice, Screen } from './shared';

/**
 * What you own.
 *
 * Hard rule 20: nobody else can see this, and that is not a default that can
 * be toggled. The API has no route that names a user, so there is nothing here
 * to get wrong — the endpoint returns the caller's library or refuses.
 */
export function LibraryRoute(): React.ReactElement {
  const query = useApi<{ entries: LibraryEntry[]; count: number }>(['library'], '/me/library');
  const shelf = useShelf();

  const artBySlug = new Map(
    (shelf.data?.entries ?? []).map((entry) => [entry.listing.slug, toCapsule(entry)]),
  );

  const owned = (query.data?.entries ?? []).map((entry) =>
    ownedCapsuleFor(entry, artBySlug.get(entry.productSlug) ?? null),
  );

  return (
    <Screen title="Library" query={query} aside={`${owned.length} owned`}>
      <div className={ui.stack ?? ''}>
        <RedeemGift />

        {query.data && owned.length === 0 ? (
          <Empty>
            Nothing here yet. <Link href={'/'}>Look at our games</Link>, or use a gift key. To play
            them you need <Link href={'/download'}>the launcher</Link>.
          </Empty>
        ) : (
          <CapsuleGrid titles={owned} portrait />
        )}
      </div>
    </Screen>
  );
}

function ownedCapsuleFor(entry: LibraryEntry, known: CapsuleTitle | null): CapsuleTitle {
  return {
    slug: entry.productSlug,
    name: entry.productName,
    studio: known?.studio ?? '',
    price: null,
    href: `/library/${entry.productSlug}`,
    artUrl: known?.artUrl ?? null,
    portraitArtUrl: known?.portraitArtUrl ?? null,
    heroArtUrl: known?.heroArtUrl ?? null,
    logoUrl: known?.logoUrl ?? null,
    liveViewers: null,
    releaseLabel: stateOf(entry),
  };
}

/**
 * Claiming a game somebody bought for you (#74).
 *
 * **On the Library page rather than a page of its own**, and always visible
 * rather than only when the library is empty. Somebody handed a key does not go
 * looking for a *Gifts* screen; they go to the place their games are, which is
 * also the place the game will appear a second later. And the empty-library
 * case is the least likely one — the common gift recipient is a player who
 * already owns things.
 *
 * **Every refusal is the server's own sentence.** *That gift has already been
 * claimed*, *you already own this, so pass it on*, *that was refunded* — each is
 * the whole answer, and a client that reworded them would be the second place
 * the rules are described. Nothing here decides anything: it posts the key and
 * renders what came back.
 */
function RedeemGift(): React.ReactElement {
  const call = useApiMutation();
  const cache = useQueryClient();

  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  async function redeem(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    setFailure(null);

    try {
      const claimed = await call<{ productName: string }>('/gifts/redeem', {
        method: 'POST',
        body: JSON.stringify({ key }),
      });

      setMessage(`${claimed.productName} is yours. It is in your library below.`);
      setKey('');
      // The library has just gained a game, and a stale list is the one thing
      // somebody would notice immediately after being told it worked.
      await cache.invalidateQueries({ queryKey: ['library'] });
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h2 className={ui.sectionTitle ?? ''}>Were you given a game?</h2>

      <p className="gb-body">
        Type in the key you were sent and the game is yours, just as if you had bought it. Whoever
        bought it can still ask for their money back. Nobody else is told you have it.
      </p>

      {message ? (
        <p className="gb-body" role="status">
          {message}
        </p>
      ) : null}

      {failure ? <Notice alert>{failure}</Notice> : null}

      <form onSubmit={(event) => void redeem(event)} className={ui.stack ?? ''}>
        <Field label="Gift key">
          <input
            className={ui.input ?? ''}
            value={key}
            onChange={(event) => setKey(event.target.value)}
            /*
             * No `pattern` and no maxlength beyond a sane bound. The only shape
             * the provider ever shows is an example rather than a
             * specification, so a regex derived from it would reject real keys
             * the day the format widens — and a client-side rule that refuses a
             * valid key is a rule nobody can argue with.
             */
            autoComplete="off"
            spellCheck={false}
            placeholder="ABC123-XYZ456-XYZ456-XYZ456"
          />
        </Field>

        <div className={ui.actions ?? ''}>
          <button
            type="submit"
            className={ui.button ?? ''}
            disabled={busy || key.trim() === ''}
            {...soundedPointerHandlers}
          >
            {busy ? 'Claiming…' : 'Claim it'}
          </button>
        </div>
      </form>

      <Rule />
    </>
  );
}

/**
 * Revocation is stated, with its reason.
 *
 * Hard rule 19: a game is never revoked as a penalty, so every reason a player
 * can see here is a refund, a chargeback, a closed wave or an unclaimed gift.
 * Showing the reason rather than only the fact is what makes that checkable by
 * the person it happened to.
 */
function stateOf(entry: LibraryEntry): string {
  if (entry.revokedAt) return `Revoked — ${entry.revokedReason?.replace(/_/g, ' ') ?? 'no reason'}`;
  if (entry.expiresAt) return `Expires ${formatDate(entry.expiresAt)}`;
  return 'Yours';
}
